import React, { useState, useRef, useCallback, useEffect } from "react";
import { useColors } from "../../constants/Colors";

interface AudioWaveformProps {
  url: string;
  waveformData: number[]; // Array of numbers between 0 and 1
  durationMs?: number; // Duration in milliseconds from message.filename
  isOwnMessage: boolean;
}

const BAR_WIDTH = 2.5;
const BAR_GAP = 2;
const BAR_COUNT = 40; // Number of bars to display
const WAVEFORM_HEIGHT = 28;

const AudioWaveform: React.FC<AudioWaveformProps> = ({
  url,
  waveformData,
  durationMs,
  isOwnMessage,
}) => {
  const colors = useColors();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(
    durationMs ? durationMs / 1000 : 0,
  );
  const [currentTime, setCurrentTime] = useState(0);
  const waveformRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);

  // Normalize waveform data to BAR_COUNT bars
  const bars = React.useMemo(() => {
    if (!waveformData || waveformData.length === 0) {
      // Generate random-ish bars as fallback
      return Array.from({ length: BAR_COUNT }, () => 0.2 + Math.random() * 0.6);
    }
    if (waveformData.length === BAR_COUNT) return waveformData;

    const result: number[] = [];
    const step = waveformData.length / BAR_COUNT;
    for (let i = 0; i < BAR_COUNT; i++) {
      const start = Math.floor(i * step);
      const end = Math.floor((i + 1) * step);
      let sum = 0;
      let count = 0;
      for (let j = start; j < end && j < waveformData.length; j++) {
        sum += waveformData[j];
        count++;
      }
      result.push(count > 0 ? sum / count : 0);
    }
    return result;
  }, [waveformData]);

  const updateProgress = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      const ct = audioRef.current.currentTime;
      const dur = audioRef.current.duration || duration;
      setCurrentTime(ct);
      if (dur > 0) setProgress(ct / dur);
      animFrameRef.current = requestAnimationFrame(updateProgress);
    }
  }, [duration]);

  useEffect(() => {
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) {
      const audio = new Audio(url);
      audioRef.current = audio;

      audio.addEventListener("loadedmetadata", () => {
        if (audio.duration && isFinite(audio.duration)) {
          setDuration(audio.duration);
        }
      });

      audio.addEventListener("ended", () => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
      });
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      animFrameRef.current = requestAnimationFrame(updateProgress);
    }
  }, [isPlaying, url, updateProgress]);

  // Seek on waveform click
  const handleWaveformClick = useCallback(
    (e: React.MouseEvent) => {
      if (!waveformRef.current || !audioRef.current) return;
      const rect = waveformRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      const dur = audioRef.current.duration || duration;
      audioRef.current.currentTime = pct * dur;
      setProgress(pct);
      setCurrentTime(pct * dur);
    },
    [duration],
  );

  const formatTime = (seconds: number) => {
    if (!isFinite(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const displayTime = isPlaying || currentTime > 0 ? currentTime : duration;

  const baseColor = isOwnMessage
    ? "rgba(255,255,255,0.4)"
    : colors.containerNeutralLightLow;
  const highlightColor = isOwnMessage
    ? "#ffffff"
    : colors.accentColor;

  return (
    <div className="flex items-center gap-2.5" style={{ minWidth: 200 }}>
      {/* Play/Pause button */}
      <button
        onClick={togglePlay}
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-transform hover:scale-110"
        style={{
          backgroundColor: isOwnMessage
            ? "rgba(255,255,255,0.2)"
            : colors.accentColor,
        }}
      >
        {isPlaying ? (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <rect x="1.5" y="1" width="3" height="10" rx="1" fill={isOwnMessage ? "#fff" : colors.textNeutralOnPrimary} />
            <rect x="7.5" y="1" width="3" height="10" rx="1" fill={isOwnMessage ? "#fff" : colors.textNeutralOnPrimary} />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 1.5L10.5 6L2.5 10.5V1.5Z" fill={isOwnMessage ? "#fff" : colors.textNeutralOnPrimary} />
          </svg>
        )}
      </button>

      {/* Waveform + duration */}
      <div className="flex-1 flex flex-col gap-1">
        <div
          ref={waveformRef}
          onClick={handleWaveformClick}
          className="flex items-center cursor-pointer"
          style={{ height: WAVEFORM_HEIGHT, gap: BAR_GAP }}
        >
          {bars.map((value, i) => {
            const isHighlighted = i / bars.length < progress;
            const barHeight = Math.max(3, value * WAVEFORM_HEIGHT);
            return (
              <div
                key={i}
                className="rounded-full flex-shrink-0 transition-colors duration-100"
                style={{
                  width: BAR_WIDTH,
                  height: barHeight,
                  backgroundColor: isHighlighted ? highlightColor : baseColor,
                }}
              />
            );
          })}
        </div>
        <span
          className="text-[10px] leading-none"
          style={{
            color: isOwnMessage
              ? "rgba(255,255,255,0.7)"
              : colors.textNeutralMuted,
          }}
        >
          {formatTime(displayTime)}
        </span>
      </div>
    </div>
  );
};

export default AudioWaveform;
